import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { DocumentContext } from 'contexts/DocumentContext';
import { GET_SUBMISSION_DOCUMENTS, UPDATE_LENDER_DOCUMENTS_ACCESS } from '../api';
import { mapToDecisionLetterRow, mapToCreditSubDocRow } from '../lib';
import { CreditSubmissionDocumentRow, SubmissionDocumentRaw, SubmissionDocumentRow } from '../types';

interface UseSubmissionDocumentsParams {
  creditSubId: string;
  voId: string;
  creditSubmissionId?: string;
}

export const useSubmissionDocuments = ({ creditSubId, voId, creditSubmissionId }: UseSubmissionDocumentsParams) => {
  const documentContext = useContext(DocumentContext);

  const { data, loading, refetch } = useQuery(GET_SUBMISSION_DOCUMENTS, {
    variables: { creditSubId, VOId: voId },
    notifyOnNetworkStatusChange: true,
  });

  const [updateLenderDocumentsAccess] = useMutation(UPDATE_LENDER_DOCUMENTS_ACCESS);

  const [csDocuments, setCsDocuments] = useState<CreditSubmissionDocumentRow[]>([]);

  const decisionLetters: SubmissionDocumentRow[] = useMemo(() => {
    if (!data?.lenderDecisionLetters) return [];
    return data.lenderDecisionLetters.map((doc: SubmissionDocumentRaw) => mapToDecisionLetterRow(doc));
  }, [data]);

  useEffect(() => {
    if (!data?.creditSubmissionDocuments) {
      setCsDocuments([]);
      return;
    }
    setCsDocuments(
      data.creditSubmissionDocuments.map((doc: SubmissionDocumentRaw) => mapToCreditSubDocRow(doc, creditSubmissionId))
    );
  }, [data, creditSubmissionId]);

  useEffect(() => {
    if (refetch) refetch();
  }, [documentContext.lastDoc, refetch]);

  const toggleLenderAccess = async (documentId: string) => {
    const updatedDocs = csDocuments.map(doc =>
      doc.id === documentId ? { ...doc, lenderAccess: doc.lenderAccess === 'true' ? 'false' : 'true' } : doc
    );
    setCsDocuments(updatedDocs);

    const targetDoc = updatedDocs.find(d => d.id === documentId);
    if (!targetDoc) return;

    await updateLenderDocumentsAccess({
      variables: {
        batchUpdateLenderDocumentAccessInput: {
          creditSubmissionId: creditSubId,
          documents: [{ documentId, lenderAccess: targetDoc.lenderAccess === 'true' }],
        },
      },
    });
  };

  return {
    isLoading: loading,
    decisionLetters,
    csDocuments,
    toggleLenderAccess,
    refetch,
  };
};
